import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import Button from '@components/atoms/ui-kit/button';
import PageHeader from '@components/layout/page-header.tsx';
import {
  useGetOrgPublishingPolicy,
  useSetOrgPublishingPolicy
} from '@utils/api/back-office/publishing/use-org-publishing-policy.tsx';

interface Props {
  customerId: string | number;
  cardStyle: React.CSSProperties;
  isReseller?: boolean;
}

const platformLabel = (id: string) =>
  id === 'ibabs' ? 'iBabs' : id === 'youtube' ? 'YouTube' : id;

/**
 * Operator control for publishing (api-scriptix operator policy). Scriptix
 * staff decide whether an organization sees publishing, which platforms it
 * may use and whether it may switch on YouTube uploads. Saved explicitly,
 * so a half-edited form never changes a customer.
 */
export function PublishingPolicyCard({
  customerId,
  cardStyle,
  isReseller = false
}: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetOrgPublishingPolicy(customerId);
  const save = useSetOrgPublishingPolicy(customerId);

  const [enabled, setEnabled] = useState(false);
  const [restrict, setRestrict] = useState(false);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [uploads, setUploads] = useState(false);
  const [children, setChildren] = useState(false);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setRestrict(data.allowed_platforms !== null);
    setPlatforms(data.allowed_platforms ?? []);
    setUploads(data.video_uploads_allowed);
  }, [data]);

  const togglePlatform = (id: string, on: boolean) =>
    setPlatforms(current =>
      on ? [...new Set([...current, id])] : current.filter(p => p !== id)
    );

  const onSave = () =>
    save.mutate(
      {
        enabled,
        allowed_platforms: restrict ? platforms : null,
        video_uploads_allowed: uploads,
        include_children: children
      },
      {
        onSuccess: () => {
          toast.success(t('publishing-policy-saved'));
        },
        onError: () => {
          toast.error(t('publishing-policy-save-failed'));
        }
      }
    );

  return (
    <div
      className="rounded-2xl border shadow-card p-4 sm:p-6"
      style={cardStyle}
      data-testid="publishing-policy-card"
    >
      <PageHeader
        title={t('publishing-policy-title')}
        className="border-b-0 mb-4"
        contentClassName="px-0 py-0"
      />
      {isLoading && <p className="text-sm">{t('loading')}</p>}
      {isError && (
        <p className="text-sm text-red-500">
          {t('publishing-policy-load-failed')}
        </p>
      )}
      {data && (
        <div className="flex flex-col gap-4">
          <Checkbox
            id="publishing_enabled"
            name="publishing_enabled"
            label={t('publishing-policy-enabled')}
            description={t('publishing-policy-enabled-description')}
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            variant="primary"
          />
          <Checkbox
            id="publishing_restrict"
            name="publishing_restrict"
            label={t('publishing-policy-restrict')}
            description={t('publishing-policy-restrict-description')}
            checked={restrict}
            disabled={!enabled}
            onChange={e => setRestrict(e.target.checked)}
            variant="primary"
          />
          {restrict && (
            <div className="flex flex-col gap-2 pl-6">
              {data.known_platforms.map(id => (
                <Checkbox
                  key={id}
                  id={`publishing_platform_${id}`}
                  name={`publishing_platform_${id}`}
                  label={platformLabel(id)}
                  checked={platforms.includes(id)}
                  disabled={!enabled}
                  onChange={e => togglePlatform(id, e.target.checked)}
                  variant="primary"
                />
              ))}
              {platforms.length === 0 && (
                <p className="text-xs">{t('publishing-policy-none-allowed')}</p>
              )}
            </div>
          )}
          <Checkbox
            id="publishing_uploads"
            name="publishing_uploads"
            label={t('publishing-policy-uploads')}
            description={t('publishing-policy-uploads-description')}
            checked={uploads}
            disabled={!enabled}
            onChange={e => setUploads(e.target.checked)}
            variant="primary"
          />
          {isReseller && (
            <Checkbox
              id="publishing_children"
              name="publishing_children"
              label={t('publishing-policy-children')}
              description={t('publishing-policy-children-description')}
              checked={children}
              onChange={e => setChildren(e.target.checked)}
              variant="primary"
            />
          )}
          <div>
            <Button
              variant="primary"
              size="small"
              label={t('publishing-policy-save')}
              onClick={onSave}
              disabled={save.isLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
}
